import { useEffect, useState } from 'react'
import type { MatchedRow } from '../types'
import { fetchMatched } from '../lib/api'
import { money, relativeTime } from '../lib/format'
import { ExportButtons } from '../components/ExportButtons'
import { useBrandFilter } from '../lib/brand'

function formatLag(seconds: number): string {
  const s = Math.abs(seconds)
  if (s < 60) return `${seconds}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${seconds < 0 ? '-' : ''}${m}m`
  const h = Math.floor(m / 60)
  return `${seconds < 0 ? '-' : ''}${h}h ${m % 60}m`
}

export function Matched() {
  const [brand] = useBrandFilter()
  const [rows, setRows] = useState<MatchedRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        setRows(null)
        setRows(await fetchMatched(200))
      } catch (e) {
        setError((e as Error).message)
      }
    })()
  }, [brand])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <h1 className="text-2xl font-semibold">Matched</h1>
          <p className="text-sm text-slate-400">
            Last 200 transactions where PSP and Omega reconcile cleanly.
          </p>
        </div>
        <ExportButtons name="matched" rows={rows as unknown as Record<string, unknown>[] | null} />
      </div>

      <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 text-sm text-slate-300">
        <span className="text-slate-100 font-medium">Lag</span> = time between
        when the PSP captured the charge and when Omega finished processing
        the deposit. Small lag (seconds–minutes) means the two systems are in
        sync. A large or negative lag means Omega&apos;s replica is running
        behind real time — detection still works, we just wait until Omega
        catches up.
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-200 rounded px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="border border-slate-800 rounded-lg p-8 text-center text-slate-400">
          Nothing matched yet — check back after the next Omega pull.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="border border-slate-800 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
              <tr className="whitespace-nowrap">
                {brand === 'ALL' && <th className="text-left px-3 py-2">Brand</th>}
                <th className="text-left px-3 py-2">Ref</th>
                <th className="text-left px-3 py-2">Party ID</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">PSP</th>
                <th className="text-left px-3 py-2">External ID</th>
                <th className="text-right px-3 py-2">PSP $</th>
                <th className="text-right px-3 py-2">Omega $</th>
                <th className="text-left px-3 py-2">Captured</th>
                <th className="text-left px-3 py-2">Processed</th>
                <th className="text-right px-3 py-2">Lag</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.brand}-${r.psp}-${r.reference}`} className="border-t border-slate-800/80">
                  {brand === 'ALL' && (
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${r.brand === 'ROLLA' ? 'bg-red-500/20 text-red-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
                        {r.brand}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono">{r.reference}</td>
                  <td className="px-3 py-2 font-mono text-slate-300">
                    {r.party_id ?? '—'}
                  </td>
                  <td
                    className="px-3 py-2 text-slate-300 max-w-[220px] truncate"
                    title={r.customer_email ?? ''}
                  >
                    {r.customer_email ?? '—'}
                  </td>
                  <td className="px-3 py-2 uppercase">{r.psp}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">
                    {r.external_id}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {money(r.paycom_amount)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {money(r.omega_amount)}
                  </td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                    {relativeTime(r.captured_at)}
                  </td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                    {relativeTime(r.process_date)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-400 whitespace-nowrap">
                    {formatLag(r.lag_seconds)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
